import React, { useEffect, useRef, useState } from 'react';
import { buildApiHostUrl } from '../utils/apiConfig';

import './AccessibleControls.css';
import './PublicFileAccess.css';

export default function PublicFileAccess() {
  const [state, setState] = useState({ status: 'loading', error: '', filename: '' });
  const [attempt, setAttempt] = useState(0);
  const frameRef = useRef(null);
  const parts = window.location.pathname.split('/').filter(Boolean);
  const code = parts.length === 2 && parts[0] === 'f' ? parts[1] : '';
  const downloadUrl = code ? buildApiHostUrl(`f/${encodeURIComponent(decodeCode(code))}/`) : '';

  useEffect(() => {
    if (!downloadUrl) {
      setState({ status: 'error', error: 'This file link is incomplete. Ask the sender for a new link.' });
      return undefined;
    }
    const controller = new AbortController();
    let cancelled = false;
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    setState({ status: 'loading', error: '', filename: '' });
    const checkFile = async () => {
      try {
        const response = await fetch(`${downloadUrl}?metadata=1`, {
          signal: controller.signal, cache: 'no-store', credentials: 'omit',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) {
          const message = response.status === 410
            ? 'This file link has expired. Ask the sender for a new link.'
            : response.status === 404 || response.status === 403
              ? 'This file is no longer available. Ask the sender to share it again.'
              : 'We could not check this file right now. Please try again.';
          throw new Error(message);
        }
        const info = await response.json();
        if (!info || typeof info.original_filename !== 'string') throw new Error('We could not check this file right now. Please try again.');
        if (cancelled) return;
        setState({ status: 'requested', filename: info.original_filename, error: '' });
        // Keep this recovery page visible if storage later fails or the browser
        // blocks automatic downloads. Never buffer a large file in JS memory.
        if (frameRef.current) frameRef.current.src = downloadUrl;
      } catch (error) {
        if (cancelled) return;
        setState({ status: 'error', error: error.name === 'AbortError' || error instanceof TypeError
          ? 'We could not reach the file service. Check your connection and try again.'
          : error.message });
      } finally {
        window.clearTimeout(timeout);
      }
    };
    void checkFile();
    return () => { cancelled = true; controller.abort(); window.clearTimeout(timeout); };
  }, [attempt, downloadUrl]);

  return (
    <main className="public-file-page ux-controls">
      <section className="public-file-panel" aria-labelledby="public-file-title">
        <h1 id="public-file-title">{state.status === 'loading' ? 'Checking your file' : state.status === 'error' ? 'File unavailable' : 'Download requested'}</h1>
        {state.status === 'loading' && <p role="status">Checking that this link and file are available…</p>}
        {state.status === 'error' && <>
          <p role="alert">{state.error}</p>
          {downloadUrl && <button type="button" className="public-file-action" onClick={() => setAttempt((value) => value + 1)}>Try again</button>}
        </>}
        {state.status === 'requested' && <>
          <p className="public-file-name">{state.filename}</p>
          <p role="status">Your browser has been asked to download this file. Check your browser's downloads to confirm it arrived.</p>
          <p>If it does not start, use the manual link. If that fails, check availability again or ask the sender for a new link.</p>
          <div className="public-file-actions">
          <a className="public-file-action" href={downloadUrl} target="_blank" rel="noopener noreferrer">Manual Download</a>
          <button type="button" onClick={() => setAttempt((value) => value + 1)} className="public-file-action public-file-secondary">Check again</button>
          </div>
        </>}
        <p className="public-file-home"><a href="/">Return to Home</a></p>
      </section>
      <iframe ref={frameRef} title="File download" hidden />
    </main>
  );
}

function decodeCode(code) {
  try { return decodeURIComponent(code); } catch { return code; }
}
